import { createContext, useState, useMemo, useEffect, useCallback } from 'react';
import { createTheme } from '@mui/material/styles';
import { tokens } from './themeTokens';
import profileService from './services/profileService';

export const ColorModeContext = createContext({
  toggleColorMode: () => {},
  setMode: () => {},
  reloadThemeFromBackend: () => {},
  mode: 'light',
});

export const useMode = (initialMode = 'light') => {
  // Try to get saved theme from localStorage first (for quick initial render)
  const getSavedTheme = () => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark' || saved === 'light' ? saved : initialMode;
  };

  const [mode, setModeState] = useState(getSavedTheme);

  // Persist theme to localStorage and optionally to backend
  const persistTheme = useCallback(async (newMode) => {
    localStorage.setItem('theme', newMode);

    // Try to save to backend if user is logged in
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        await profileService.updateTheme(newMode);
      } catch (error) {
        // Silently fail - localStorage will still work
        console.debug('Could not save theme to backend:', error);
      }
    }
  }, []);

  // Function to reload theme from backend (called after login)
  const reloadThemeFromBackend = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const preferences = await profileService.getPreferences();
        if (preferences?.theme && (preferences.theme === 'light' || preferences.theme === 'dark')) {
          setModeState(preferences.theme);
          localStorage.setItem('theme', preferences.theme);
        }
      } catch (error) {
        console.debug('Could not load theme from backend:', error);
      }
    }
  }, []);

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setModeState((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          persistTheme(newMode);
          return newMode;
        });
      },
      setMode: (newMode) => {
        if (newMode === 'light' || newMode === 'dark') {
          setModeState(newMode);
          persistTheme(newMode);
        }
      },
      reloadThemeFromBackend,
    }),
    [mode, persistTheme, reloadThemeFromBackend]
  );

  // Load theme from backend on mount if user is logged in
  useEffect(() => {
    const loadThemeFromBackend = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const preferences = await profileService.getPreferences();
          if (preferences?.theme && preferences.theme !== mode) {
            setModeState(preferences.theme);
            localStorage.setItem('theme', preferences.theme);
          }
        } catch (error) {
          // Use localStorage theme as fallback
          console.debug('Could not load theme from backend:', error);
        }
      }
    };

    loadThemeFromBackend();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const theme = useMemo(() => {
    const colors = tokens(mode);

    return createTheme({
      palette: {
        mode,
        ...(mode === 'dark'
          ? {
              // Dark mode palette - Muted enterprise theme
              primary: {
                main: colors.primary[500],
                light: colors.primary[400],
                dark: colors.primary[600],
                contrastText: '#ffffff',
              },
              secondary: {
                main: colors.chart.teal,
                light: colors.chart.cyan,
                dark: colors.chart.indigo,
                contrastText: '#ffffff',
              },
              error: {
                main: colors.status.error,
                light: '#f87171',
                dark: '#b91c1c',
                contrastText: '#ffffff',
              },
              warning: {
                main: colors.status.warning,
                light: '#fbbf24',
                dark: '#b45309',
                contrastText: '#000000',
              },
              info: {
                main: colors.status.info,
                light: '#60a5fa',
                dark: '#1d4ed8',
                contrastText: '#ffffff',
              },
              success: {
                main: colors.status.success,
                light: '#34d399',
                dark: '#047857',
                contrastText: '#ffffff',
              },
              background: {
                default: colors.background.default,
                paper: colors.background.paper,
              },
              text: {
                primary: '#f8fafc',
                secondary: '#a1a1aa',
                disabled: '#52525b',
              },
              divider: colors.border.default,
              action: {
                active: '#f8fafc',
                hover: 'rgba(255, 255, 255, 0.08)',
                selected: 'rgba(255, 255, 255, 0.12)',
                disabled: 'rgba(255, 255, 255, 0.26)',
                disabledBackground: 'rgba(255, 255, 255, 0.08)',
              },
            }
          : {
              // Light mode palette - Crisp and clean
              primary: {
                main: colors.primary[600],
                light: colors.primary[500],
                dark: colors.primary[700],
                contrastText: '#ffffff',
              },
              secondary: {
                main: colors.chart.teal,
                light: colors.chart.cyan,
                dark: colors.chart.indigo,
                contrastText: '#ffffff',
              },
              error: {
                main: colors.status.error,
                light: '#f87171',
                dark: '#b91c1c',
                contrastText: '#ffffff',
              },
              warning: {
                main: colors.status.warning,
                light: '#fbbf24',
                dark: '#b45309',
                contrastText: '#ffffff',
              },
              info: {
                main: colors.status.info,
                light: '#60a5fa',
                dark: '#1d4ed8',
                contrastText: '#ffffff',
              },
              success: {
                main: colors.status.success,
                light: '#34d399',
                dark: '#047857',
                contrastText: '#ffffff',
              },
              background: {
                default: colors.background.default,
                paper: colors.background.paper,
              },
              text: {
                primary: '#0f172a',
                secondary: '#475569',
                disabled: '#94a3b8',
              },
              divider: colors.border.default,
              action: {
                active: '#0f172a',
                hover: 'rgba(0, 0, 0, 0.04)',
                selected: 'rgba(0, 0, 0, 0.08)',
                disabled: 'rgba(0, 0, 0, 0.26)',
                disabledBackground: 'rgba(0, 0, 0, 0.08)',
              },
            }),
      },
      typography: {
        fontFamily: [
          'Inter',
          '-apple-system',
          'BlinkMacSystemFont',
          '"Segoe UI"',
          'Roboto',
          '"Helvetica Neue"',
          'Arial',
          'sans-serif',
          '"Apple Color Emoji"',
          '"Segoe UI Emoji"',
          '"Segoe UI Symbol"',
        ].join(','),
        fontSize: 13,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 600,
        h1: {
          fontSize: '1.75rem',
          fontWeight: 700,
          lineHeight: 1.2,
          letterSpacing: '-0.02em',
        },
        h2: {
          fontSize: '1.5rem',
          fontWeight: 700,
          lineHeight: 1.25,
          letterSpacing: '-0.01em',
        },
        h3: {
          fontSize: '1.25rem',
          fontWeight: 600,
          lineHeight: 1.3,
          letterSpacing: '-0.01em',
        },
        h4: {
          fontSize: '1.125rem',
          fontWeight: 600,
          lineHeight: 1.4,
        },
        h5: {
          fontSize: '1rem',
          fontWeight: 600,
          lineHeight: 1.5,
        },
        h6: {
          fontSize: '0.9375rem',
          fontWeight: 600,
          lineHeight: 1.6,
        },
        subtitle1: {
          fontSize: '0.9375rem',
          fontWeight: 500,
          lineHeight: 1.75,
          letterSpacing: '0.00938em',
        },
        subtitle2: {
          fontSize: '0.8125rem',
          fontWeight: 500,
          lineHeight: 1.57,
          letterSpacing: '0.00714em',
        },
        body1: {
          fontSize: '0.8125rem',
          fontWeight: 400,
          lineHeight: 1.6,
          letterSpacing: '0.00938em',
        },
        body2: {
          fontSize: '0.75rem',
          fontWeight: 400,
          lineHeight: 1.5,
          letterSpacing: '0.01071em',
        },
        button: {
          fontSize: '0.8125rem',
          fontWeight: 500,
          lineHeight: 1.75,
          textTransform: 'none',
          letterSpacing: '0.02857em',
        },
        caption: {
          fontSize: '0.6875rem',
          fontWeight: 400,
          lineHeight: 1.66,
          letterSpacing: '0.03333em',
        },
        overline: {
          fontSize: '0.6875rem',
          fontWeight: 600,
          lineHeight: 2.66,
          letterSpacing: '0.08333em',
          textTransform: 'uppercase',
        },
      },
      spacing: 8,
      shape: {
        borderRadius: 6,
      },
      shadows: mode === 'dark'
        ? [
            'none',
            '0 1px 3px 0 rgba(0, 0, 0, 0.7), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 4px 6px -2px rgba(0, 0, 0, 0.7), 0 10px 15px -3px rgba(0, 0, 0, 0.3)',
            '0 10px 15px -3px rgba(0, 0, 0, 0.7), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
            '0 15px 25px -5px rgba(0, 0, 0, 0.7), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
            '0 20px 25px -5px rgba(0, 0, 0, 0.7), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
            '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 2px 4px -1px rgba(0, 0, 0, 0.7), 0 4px 6px -1px rgba(0, 0, 0, 0.3)',
            '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
          ]
        : [
            'none',
            '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            '0 15px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          ],
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              scrollbarColor: mode === 'dark' ? '#6b6b6b #2b2b2b' : '#c1c1c1 #f1f1f1',
              '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                width: '12px',
                height: '12px',
              },
              '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
                borderRadius: 8,
                backgroundColor: mode === 'dark' ? '#6b6b6b' : '#c1c1c1',
                minHeight: 24,
                border: mode === 'dark' ? '3px solid #2b2b2b' : '3px solid #f1f1f1',
              },
              '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
                backgroundColor: mode === 'dark' ? '#8b8b8b' : '#a1a1a1',
              },
              '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
                borderRadius: 8,
                backgroundColor: mode === 'dark' ? '#2b2b2b' : '#f1f1f1',
              },
            },
            // Visible keyboard focus rings app-wide (WCAG 2.4.7). Scoped to
            // :focus-visible so mouse/touch interactions stay clean.
            'a:focus-visible, button:focus-visible, [role="button"]:focus-visible, [tabindex]:focus-visible, .MuiButtonBase-root:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible': {
              outline: `2px solid ${mode === 'dark' ? '#60a5fa' : '#1d4ed8'}`,
              outlineOffset: '2px',
              borderRadius: '4px',
            },
            // Respect the user's reduced-motion preference app-wide (WCAG 2.3.3).
            '@media (prefers-reduced-motion: reduce)': {
              '*, *::before, *::after': {
                animationDuration: '0.01ms !important',
                animationIterationCount: '1 !important',
                transitionDuration: '0.01ms !important',
                scrollBehavior: 'auto !important',
              },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 6,
              padding: '5px 14px',
              fontWeight: 500,
              fontSize: '0.8125rem',
              textTransform: 'none',
              boxShadow: 'none',
              transition: 'background-color 0.15s ease-in-out',
              '&:hover': {
                boxShadow: 'none',
              },
              '&:active': {
                transform: 'scale(0.98)',
              },
            },
            contained: {
              '&:hover': {
                boxShadow: mode === 'dark'
                  ? '0 4px 12px rgba(0, 0, 0, 0.5)'
                  : '0 4px 12px rgba(0, 0, 0, 0.1)',
              },
            },
            outlined: {
              borderWidth: '1px',
              '&:hover': {
                borderWidth: '1px',
              },
            },
            sizeSmall: {
              padding: '3px 10px',
              fontSize: '0.75rem',
            },
            sizeLarge: {
              padding: '7px 20px',
              fontSize: '0.875rem',
            },
          },
          defaultProps: {
            disableElevation: true,
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 6,
              backgroundImage: 'none',
              border: `1px solid ${colors.border.default}`,
              boxShadow: 'none',
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
            },
            rounded: {
              borderRadius: 8,
            },
            outlined: {
              border: `1px solid ${colors.border.default}`,
            },
            elevation1: {
              boxShadow: mode === 'dark'
                ? '0 1px 3px rgba(0, 0, 0, 0.5)'
                : '0 1px 3px rgba(0, 0, 0, 0.05)',
            },
            elevation2: {
              boxShadow: mode === 'dark'
                ? '0 2px 6px rgba(0, 0, 0, 0.6)'
                : '0 2px 6px rgba(0, 0, 0, 0.08)',
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-root': {
                borderRadius: 6,
                backgroundColor: mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.02)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-focused': {
                  backgroundColor: 'transparent',
                  boxShadow: mode === 'dark' 
                    ? `0 0 0 2px rgba(59, 130, 246, 0.25)` 
                    : `0 0 0 2px rgba(37, 99, 235, 0.2)`,
                },
              },
            },
          },
          defaultProps: {
            size: 'small',
          },
        },
        MuiSelect: {
          defaultProps: {
            size: 'small',
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              borderRadius: 0,
              backgroundImage: 'none',
              borderRight: `1px solid ${colors.border.default}`,
              backgroundColor: mode === 'dark' ? 'rgba(10, 10, 10, 0.85)' : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(12px)',
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              boxShadow: mode === 'dark'
                ? '0 4px 12px rgba(0, 0, 0, 0.5)'
                : '0 4px 12px rgba(0, 0, 0, 0.05)',
              borderBottom: `1px solid ${colors.border.default}`,
              backgroundImage: 'none',
              backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.65)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(16px)',
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              borderBottom: `1px solid ${colors.border.muted}`,
              padding: '8px 12px',
              fontSize: '0.8rem',
              // Tabular figures: numeric columns align and don't jitter on update
              // (only affects digit glyphs, so text cells are unchanged).
              fontVariantNumeric: 'tabular-nums',
            },
            head: {
              fontWeight: 600,
              fontSize: '0.75rem',
              backgroundColor: mode === 'dark'
                ? colors.surface.hover
                : colors.surface.hover,
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&:hover': {
                backgroundColor: mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.03)'
                  : 'rgba(0, 0, 0, 0.02)',
              },
              '&:nth-of-type(odd)': {
                backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(0, 0, 0, 0.01)',
              },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: 4,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
            },
            outlined: {
              borderWidth: '1px',
            },
            sizeSmall: {
              height: 20,
              fontSize: '0.6875rem',
            },
          },
        },
        MuiAlert: {
          styleOverrides: {
            root: {
              borderRadius: 6,
              fontSize: '0.875rem',
              fontWeight: 500,
            },
            standardSuccess: {
              backgroundColor: colors.status.successBg,
              color: colors.status.success,
            },
            standardError: {
              backgroundColor: colors.status.errorBg,
              // MEASURED, not chosen. `colors.status.error` (#dc2626) on the error background
              // (#fee2e2) is 3.95:1 — below WCAG AA's 4.5:1 for normal text, and it applied to
              // every error alert in the product. Red-700 on the same background is 5.30:1.
              //
              // The background stays as it is, so error alerts still LOOK the same; only the
              // text darkens. Found by running axe in a real browser against the rendered
              // components — the same check in jsdom cannot compute a colour at all.
              color: mode === 'dark' ? colors.status.error : '#b91c1c',
            },
            standardWarning: {
              backgroundColor: colors.status.warningBg,
              color: colors.status.warning,
            },
            standardInfo: {
              backgroundColor: colors.status.infoBg,
              color: colors.status.info,
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              backgroundColor: mode === 'dark' ? 'rgba(18, 18, 18, 0.85)' : 'rgba(25, 25, 25, 0.9)',
              backdropFilter: 'blur(8px)',
              border: `1px solid ${colors.border.muted}`,
              fontSize: '0.75rem',
              fontWeight: 500,
              padding: '6px 12px',
              borderRadius: 6,
            },
          },
        },
        MuiBreadcrumbs: {
          styleOverrides: {
            root: {
              fontSize: '0.875rem',
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8125rem',
              minHeight: 40,
              padding: '8px 14px',
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              borderRadius: 6,
              '&.Mui-selected': {
                backgroundColor: mode === 'dark'
                  ? 'rgba(88, 166, 255, 0.16)'
                  : 'rgba(9, 105, 218, 0.08)',
                '&:hover': {
                  backgroundColor: mode === 'dark'
                    ? 'rgba(88, 166, 255, 0.24)'
                    : 'rgba(9, 105, 218, 0.12)',
                },
              },
            },
          },
        },
        MuiSwitch: {
          styleOverrides: {
            root: {
              padding: 8,
            },
            track: {
              borderRadius: 11,
            },
            thumb: {
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            },
          },
        },
      },
    });
  }, [mode]);

  return [theme, colorMode];
};